import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { entriesApi } from '../../../../api/entries';
import { projectsApi } from '../../../../api/projects';
import { exportsApi, saveBlob, ExportError } from '../../../../api/exports';
import { TimeEntry, Project, TimeEntryStatus, PermissionEnum } from '../../../../types';
import { formatDate, getDayName } from '../../../../lib/utils';
import { useAuth } from '../../../../contexts/AuthContext';
import { Button } from '../../../../components/ui/atoms/button';
import { StatusPill } from '../../../../components/ui/atoms/status-pill';
import { Input } from '../../../../components/ui/atoms/input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../../components/ui/molecules/table';
import { CollapsibleBandRow, ExpandCollapseAll } from '../../../../components/ui/molecules/collapsible-band';
import { useCollapsibleGroups } from '../../../../lib/useCollapsibleGroups';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../../components/ui/molecules/select';
import { SearchableSelect } from '../../../../components/ui/molecules/searchable-select';
import { UserCombobox } from '../../../../components/ui/molecules/user-combobox';
import { Dialog } from '../../../../components/ui/organisms/dialog';
import { STATUS_CONFIG, WIDEST_STATUS_LABEL } from '../../../../components/ui/organisms/calendar-view';
import { NoteDialog } from '../../../../components/ui/organisms/note-dialog';
import { Loading, AdminEntryActions } from '../components';
import {
  parseWeekFilter, groupEntriesByWeekAndDate, getSortedWeekNumbers, getPhaseName,
} from '../utils';
import { ExportDialog, ExportDocument, ExportOptions } from './ExportDialog';

export function AllEntriesTab() {
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const isAdmin = hasPermission(PermissionEnum.IS_ADMIN);
  const canExport = hasPermission(PermissionEnum.EXPORT_DATA);
  const [noteDialog, setNoteDialog] = useState<{ entry: TimeEntry; action: 'request-edit' | 'reject' } | null>(null);
  const [page, setPage] = useState(1);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [weekFilter, setWeekFilter] = useState('');
  const [userId, setUserId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [status, setStatus] = useState('');
  const [entryId, setEntryId] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportMessage, setExportMessage] = useState('');
  const [exportError, setExportError] = useState('');

  const handleWeekFilter = (val: string) => {
    setWeekFilter(val);
    const range = parseWeekFilter(val, new Date().getFullYear());
    if (!range) return;
    setFrom(range.from);
    setTo(range.to);
    setPage(1);
  };

  const { data, isLoading } = useQuery({
    queryKey: ['admin-entries', page, from, to, userId, projectId, status, entryId],
    queryFn: () =>
      entriesApi
        .adminList({
          page,
          limit: 20,
          id: entryId || undefined,
          from: from || undefined,
          to: to || undefined,
          userId: userId || undefined,
          projectId: projectId || undefined,
          status: status || undefined,
        })
        .then((r) => r.data),
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list().then((r) => r.data),
  });

  const entries: TimeEntry[] = data?.data ?? [];
  const meta = data?.meta;
  const projects: Project[] = projectsData ?? [];

  const grouped = groupEntriesByWeekAndDate(entries);
  const weekNumbers = getSortedWeekNumbers(grouped);
  const groups = useCollapsibleGroups(weekNumbers.map(String));

  const reset = () => {
    setFrom(''); setTo(''); setWeekFilter(''); setUserId(''); setProjectId(''); setStatus(''); setEntryId(''); setPage(1);
  };

  // Every document is built by the backend from the on-screen filters.
  const handleExport = async (documentType: ExportDocument, options?: ExportOptions) => {
    setExportMessage('');
    setExportError('');

    // Placeholder option: produces no file.
    if (documentType === 'SAP') {
      setExportMessage('SAP template not provided yet');
      return;
    }

    setIsExporting(true);
    try {
      const filters = { id: entryId, from, to, userId, projectId };
      const file = documentType === 'timesheet'
        // A billing document: approved entries only, whatever status the screen shows.
        ? await exportsApi.customerTimesheet({ ...filters, clientName: options?.clientName })
        // Entry List / CSV show what you see, status filter included.
        : await exportsApi.entries({ ...filters, status }, documentType === 'csv' ? 'csv' : 'xlsx');
      saveBlob(file.blob, file.filename);
      setExportMessage(file.rows ? `Exported ${file.rows} entries successfully.` : 'Export downloaded.');
    } catch (error) {
      // "Nothing matches" is a normal outcome, not a failure.
      if (error instanceof ExportError && error.status === 404) setExportMessage(error.message);
      else setExportError(error instanceof ExportError ? error.message : 'Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Entry ID</label>
          <Input
            placeholder="Paste entry ID…"
            value={entryId}
            onChange={(e) => { setEntryId(e.target.value); setPage(1); }}
            className="w-64 font-mono text-xs"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Week #</label>
          <Input
            type="number"
            min={1}
            max={53}
            placeholder="e.g. 25"
            value={weekFilter}
            onChange={(e) => handleWeekFilter(e.target.value)}
            className="w-24"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">From</label>
          <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setWeekFilter(''); setPage(1); }} className="w-36" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">To</label>
          <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setWeekFilter(''); setPage(1); }} className="w-36" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Employee</label>
          <UserCombobox
            value={userId}
            onValueChange={(v) => { setUserId(v); setPage(1); }}
            allowEmpty
            emptyLabel="All employees"
            placeholder="All employees"
            className="w-44"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Project</label>
          <SearchableSelect
            value={projectId}
            onValueChange={(v) => { setProjectId(v); setPage(1); }}
            options={projects.map((p) => ({ value: p.id, label: p.name }))}
            allowEmpty
            emptyLabel="All projects"
            placeholder="All projects"
            searchPlaceholder="Search projects..."
            className="w-44"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Status</label>
          <Select value={status} onValueChange={(v) => { setStatus(v === '__all__' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All statuses</SelectItem>
              {(Object.keys(STATUS_CONFIG) as TimeEntryStatus[]).map((s) => (
                <SelectItem key={s} value={s}>{STATUS_CONFIG[s].label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {(from || to || weekFilter || userId || projectId || status || entryId) && (
          <Button variant="ghost" size="sm" onClick={reset}>Clear</Button>
        )}
        <div className="flex items-end ml-auto">
          <ExpandCollapseAll
            allExpanded={groups.allExpanded}
            onExpandAll={groups.expandAll}
            onCollapseAll={groups.collapseAll}
            disabled={weekNumbers.length === 0}
          />
        </div>
        {canExport && (
          <div className="flex items-end">
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setExportMessage(''); setExportError(''); setExportOpen(true); }}
              disabled={isExporting || isLoading}
            >
              <Download className="mr-2 h-4 w-4" />
              {isExporting ? 'Extracting…' : 'Extract All'}
            </Button>
          </div>
        )}
      </div>

      <ExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        onExport={handleExport}
        isExporting={isExporting}
        entryCount={entries.length}
        defaultClientName={projects.find((p) => p.id === projectId)?.client ?? ''}
        message={exportMessage}
        error={exportError}
      />

      {/* Export feedback lives inside the dialog — on the page it sat behind
          the modal overlay and was never seen. */}

      {isLoading ? (
        <Loading />
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Project</TableHead>
                <TableHead>Phase</TableHead>
                <TableHead>Hours</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Notes</TableHead>
                {isAdmin && <TableHead className="w-12" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 7 : 6} className="text-center text-muted-foreground">
                    No entries found.
                  </TableCell>
                </TableRow>
              )}
              {weekNumbers.map((week) => {
                const dayMap = grouped[week];
                const dates = Object.keys(dayMap).sort((a, b) => b.localeCompare(a));
                const weekTotal = dates.reduce((s, d) => s + dayMap[d].reduce((s2, e) => s2 + e.hours, 0), 0);
                return (
                  <React.Fragment key={week}>
                    <CollapsibleBandRow
                      colSpan={isAdmin ? 7 : 6}
                      expanded={groups.isExpanded(String(week))}
                      onToggle={() => groups.toggle(String(week))}
                    >
                      <span className="font-semibold text-sm">Week {week}</span>
                      <span className="ml-3 text-xs opacity-70">{weekTotal}h total</span>
                    </CollapsibleBandRow>
                    {groups.isExpanded(String(week)) && dates.map((dateStr) => {
                      const dayEntries = dayMap[dateStr];
                      const dayTotal = dayEntries.reduce((s, e) => s + e.hours, 0);
                      return (
                        <React.Fragment key={dateStr}>
                          <TableRow className="bg-muted/25 hover:bg-muted/25">
                            <TableCell colSpan={isAdmin ? 7 : 6} className="py-1 pl-6">
                              <span className="text-sm font-medium">{getDayName(dateStr)}</span>
                              <span className="ml-2 text-xs text-muted-foreground">{formatDate(dateStr + 'T00:00:00')}</span>
                              <span className="ml-3 text-xs text-muted-foreground">{dayTotal}h</span>
                            </TableCell>
                          </TableRow>
                          {dayEntries.map((entry) => {
                            const status = STATUS_CONFIG[entry.status];
                            return (
                              <TableRow key={entry.id}>
                                <TableCell>
                                  <div className="font-medium">{entry.user?.name ?? '—'}</div>
                                  <div className="text-xs text-muted-foreground">{entry.user?.email}</div>
                                </TableCell>
                                <TableCell>{entry.project?.name ?? '—'}</TableCell>
                                <TableCell className="text-muted-foreground">{getPhaseName(projects, entry)}</TableCell>
                                <TableCell>{entry.hours}h</TableCell>
                                <TableCell>
                                  <StatusPill {...status} sizeTo={WIDEST_STATUS_LABEL} />
                                </TableCell>
                                <TableCell className="max-w-xs truncate text-muted-foreground">{entry.notes ?? '—'}</TableCell>
                                {isAdmin && (
                                  <TableCell>
                                    <AdminEntryActions
                                      entry={entry}
                                      onRequestNote={(e, action) => setNoteDialog({ entry: e, action })}
                                    />
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>

          {meta && meta.totalPages > 1 && (
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {meta.page} of {meta.totalPages}
              </span>
              <Button variant="outline" size="sm" disabled={page >= meta.totalPages} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </div>
          )}
        </>
      )}

      <Dialog open={!!noteDialog} onOpenChange={(open) => !open && setNoteDialog(null)}>
        {noteDialog && (
          <NoteDialog
            entry={noteDialog.entry}
            action={noteDialog.action}
            onClose={() => setNoteDialog(null)}
            onSuccess={() => queryClient.invalidateQueries({ queryKey: ['admin-entries'] })}
            title={noteDialog.action === 'reject' ? 'Reject Entry (Admin Override)' : 'Request Edit (Admin Override)'}
            description="This bypasses the normal approval chain and applies regardless of the entry's current status."
          />
        )}
      </Dialog>
    </div>
  );
}
